import type { SingleServiceProvider } from '../service-providers/service-providers.dto.ts';
import type { HomePort } from '../vessels/vessel.dto.ts';

import type { Berth } from './vessel-marinas.dto.ts';

/**
 * The home port lives in `vessel.home_port` and nowhere else, a copy of the marina's
 * location. `place_id` names the marina it came from — `berth:<id>` or `sp:<id>` — and
 * the backend only passes it through. Anything else there is a name typed in the
 * questionnaire or an older Google place: not linked to My Marinas.
 *
 * Type-only imports: `node --test` runs this file and cannot resolve `@/`.
 */
export type HomePortKey = `berth:${string}` | `sp:${string}`;

export type StarredMarina = Pick<SingleServiceProvider, 'id' | 'name' | 'locations'>;

export const berthKey = (id: string): HomePortKey => `berth:${id}`;
export const starredKey = (id: string): HomePortKey => `sp:${id}`;

export const homePortKey = (port?: HomePort | null): HomePortKey | undefined => {
  const id = port?.place_id;
  return id && /^(berth|sp):./.test(id) ? (id as HomePortKey) : undefined;
};

/** The home port's name when it comes from My Marinas: the questionnaire shows it read-only. */
export const linkedHomePortName = (port?: HomePort | null) =>
  homePortKey(port) ? port?.name : undefined;

export const berthHomePort = (berth: Berth): HomePort => ({
  name: berth.title,
  address: berth.location.address ?? '',
  city: berth.location.city ?? '',
  state: '',
  zip_code: '',
  latitude: berth.location.latitude,
  longitude: berth.location.longitude,
  place_id: berthKey(berth.id),
});

export const starredHomePort = (marina: StarredMarina): HomePort => {
  const location = marina.locations[0]?.location;
  return {
    name: marina.name,
    address: location?.address ?? '',
    city: location?.city ?? '',
    state: location?.state ?? '',
    zip_code: location?.zip_code ?? '',
    latitude: location?.latitude ?? 0,
    longitude: location?.longitude ?? 0,
    place_id: starredKey(marina.id),
  };
};

/** A home port typed in by name: no place to link to or put on a map. */
export const typedHomePort = (name: string): HomePort => ({
  name,
  address: '',
  city: '',
  state: '',
  zip_code: '',
  latitude: 0,
  longitude: 0,
  place_id: '',
});

/**
 * `home_port` once the questionnaire's answer for it is saved: a marina stays (the
 * question is read-only then), the stored name keeps what is stored, another name
 * replaces it, and a cleared one clears it. No answer at all keeps it: the answers
 * were read before the vessel loaded, not emptied.
 */
export const homePortFromAnswer = (
  current: HomePort | null | undefined,
  answer?: string,
): HomePort | undefined => {
  if ((current && homePortKey(current)) || answer === undefined) return current ?? undefined;
  const name = answer.trim();
  if (!name) return undefined;
  return name === current?.name ? current : typedHomePort(name);
};

/**
 * The answers as saved. The questionnaire asks for the home port but `home_port` keeps
 * it (`homePortFromAnswer`); a copy in the answers would go stale on a rename or unset.
 */
export const withoutHomePortAnswer = <T extends { home_port?: string }>(answers: T): T => ({
  ...answers,
  home_port: undefined,
});

/** What a picker keys a home port by: the marina it links to, or the name typed in. */
export const homePortChoiceValue = (port: HomePort) => homePortKey(port) ?? `typed:${port.name}`;

/**
 * The home ports to pick from: the saved marinas, berths first, and ahead of them the
 * stored one when they lack it — typed in, or a marina a list has not caught up with —
 * so picking leaves it where it is.
 */
export const homePortChoices = ({
  current,
  berths,
  starred = [],
}: {
  current?: HomePort | null;
  berths: Berth[];
  starred?: StarredMarina[];
}) => {
  const saved = [...berths.map(berthHomePort), ...starred.map(starredHomePort)];
  const kept = current?.name && !saved.some((port) => port.place_id === current.place_id);
  return (kept ? [current, ...saved] : saved).map((port) => ({
    value: homePortChoiceValue(port),
    port,
  }));
};

const COMPARED = ['place_id', 'name', 'address', 'city', 'latitude', 'longitude'] as const;

/** `''`, `null` and `undefined` are the same: the backend may hand back either for what we sent. */
export const sameHomePort = (a?: HomePort | null, b?: HomePort | null) =>
  COMPARED.every((field) => (a?.[field] ?? '') === (b?.[field] ?? ''));

/**
 * What the sync remembers between runs. Keyed on the stored value too: a reply that never
 * compares equal stops after one retry, and a later change to the stored value is not blocked.
 */
export const syncAttempt = (current?: HomePort | null, desired?: HomePort | null) =>
  JSON.stringify([current ?? null, desired ?? null]);

/** The default: the only Permanent berth, or none. */
export const defaultHomePort = (berths: Berth[]) => {
  const permanent = berths.filter((berth) => berth.type === 'permanent');
  return permanent.length === 1 ? berthHomePort(permanent[0]) : null;
};

/**
 * What `home_port` should be: a `HomePort` to write, or `undefined` to leave it alone.
 * It refreshes a linked marina from the lists and fills an empty one with the default,
 * but never clears: a list read right after a write can still lack the marina, so only
 * a removal clears (`homePortAfterRemoval`). A typed name is a choice and stays.
 */
export const desiredHomePort = ({
  current,
  berths,
  starred,
}: {
  current?: HomePort | null;
  berths: Berth[];
  starred?: StarredMarina[];
}): HomePort | undefined => {
  const key = homePortKey(current);
  const linkedBerth = berths.find((berth) => berthKey(berth.id) === key);
  if (linkedBerth) return berthHomePort(linkedBerth);
  const marina = starred?.find((m) => starredKey(m.id) === key);
  if (marina) return starredHomePort(marina);
  if (key || current?.name) return undefined;
  return defaultHomePort(berths) ?? undefined;
};

/**
 * The home port once the marina `removed` names is deleted or unstarred: the default,
 * or `null` to clear. `undefined` when it was not the home port. Without `berths`
 * there is no default to fall back to; My Marinas fills it in when it next opens.
 */
export const homePortAfterRemoval = ({
  current,
  removed,
  berths = [],
}: {
  current?: HomePort | null;
  removed: HomePortKey;
  berths?: Berth[];
}): HomePort | null | undefined =>
  homePortKey(current) === removed
    ? defaultHomePort(berths.filter((berth) => berthKey(berth.id) !== removed))
    : undefined;

/** Where the home port is, for ranking places near it. A port typed in by name sits at 0,0: no point. */
export const homePortPoint = (port?: HomePort | null) =>
  port && (port.latitude || port.longitude)
    ? { latitude: port.latitude, longitude: port.longitude }
    : undefined;

/** The home port as a location stop: the city too, so a trip map can still geocode a bare berth name. */
export const homePortStop = (port: HomePort) => [port.name, port.city].filter(Boolean).join(', ');
